// World visitor bubble map — no external tiles (CSP-safe). Visitor rows carry
// only country/region/city text, so each row is placed by real latitude/
// longitude: India rows use city/state centroids (for city-level precision),
// every other country is placed by its country centroid. Countries we don't
// have a centroid for are counted as "unmapped" rather than mis-plotted.

// Equirectangular projection, cropped to the inhabited-world latitude band
// (matches the range most web maps default to) so the box isn't mostly
// empty ocean at the poles.
const LAT_MIN = -56;
const LAT_MAX = 78;

// Real lat/lon centroids for major Indian cities/states that appear in our
// visitor data — gives India city-level precision instead of a single dot.
const CITY_LATLON = {
  hyderabad: [17.385, 78.4867],
  bengaluru: [12.9716, 77.5946],
  bangalore: [12.9716, 77.5946],
  chennai: [13.0827, 80.2707],
  mumbai: [19.076, 72.8777],
  pune: [18.5204, 73.8567],
  delhi: [28.6139, 77.209],
  "new delhi": [28.6139, 77.209],
  kolkata: [22.5726, 88.3639],
  ahmedabad: [23.0225, 72.5714],
  visakhapatnam: [17.6868, 83.2185],
  vijayawada: [16.5062, 80.648],
  tirupati: [13.6288, 79.4192],
};

const STATE_LATLON = {
  "andhra pradesh": [15.9129, 79.74],
  telangana: [18.1124, 79.0193],
  "tamil nadu": [11.1271, 78.6569],
  karnataka: [15.3173, 75.7139],
  kerala: [10.8505, 76.2711],
  maharashtra: [19.7515, 75.7139],
  goa: [15.2993, 74.124],
  gujarat: [22.2587, 71.1924],
  rajasthan: [27.0238, 74.2179],
  "madhya pradesh": [22.9734, 78.6569],
  "uttar pradesh": [26.8467, 80.9462],
  delhi: [28.7041, 77.1025],
  haryana: [29.0588, 76.0856],
  punjab: [31.1471, 75.3412],
  bihar: [25.0961, 85.3131],
  jharkhand: [23.6102, 85.2799],
  "west bengal": [22.9868, 87.855],
  odisha: [20.9517, 85.0985],
  chhattisgarh: [21.2787, 81.8661],
  assam: [26.2006, 92.9376],
  uttarakhand: [30.0668, 79.0193],
  "himachal pradesh": [31.1048, 77.1734],
  "jammu and kashmir": [33.7782, 76.5762],
  meghalaya: [25.467, 91.3662],
  manipur: [24.6637, 93.9063],
  tripura: [23.9408, 91.9882],
  nagaland: [26.1584, 94.5624],
  sikkim: [27.533, 88.5122],
  "arunachal pradesh": [28.218, 94.7278],
  mizoram: [23.1645, 92.9376],
  chandigarh: [30.7333, 76.7794],
  puducherry: [11.9416, 79.8083],
};

// Country centroids (ISO-3166 alpha-2, matches CloudFront-viewer-country /
// ip-api countryCode values stored in the DB). Add more codes here as new
// countries show up in traffic — anything missing just falls into
// "unmapped" instead of being plotted in the wrong place.
const COUNTRY_LATLON = {
  US: [39.8, -98.6],
  CA: [56.1, -106.3],
  GB: [54.0, -2.0],
  IE: [53.4, -8.0],
  FR: [46.6, 2.2],
  DE: [51.2, 10.4],
  NL: [52.1, 5.3],
  ES: [40.0, -4.0],
  IT: [42.8, 12.5],
  PT: [39.4, -8.2],
  SE: [60.1, 18.6],
  NO: [60.5, 8.5],
  CH: [46.8, 8.2],
  AE: [23.4, 53.8],
  SA: [23.9, 45.1],
  SG: [1.35, 103.8],
  MY: [4.2, 101.9],
  JP: [36.2, 138.3],
  KR: [35.9, 127.8],
  CN: [35.9, 104.2],
  AU: [-25.3, 133.8],
  NZ: [-41.0, 174.9],
  BR: [-14.2, -51.9],
  MX: [23.6, -102.5],
  ZA: [-30.6, 22.9],
  RU: [61.5, 105.3],
  IN: [22.0, 79.0],
  PK: [30.4, 69.3],
  BD: [23.7, 90.4],
  LK: [7.9, 80.8],
  NP: [28.2, 84.0],
  PH: [12.9, 121.8],
  ID: [-0.8, 113.9],
  TH: [15.9, 100.9],
  VN: [14.1, 108.3],
  QA: [25.35, 51.2],
  KW: [29.3, 47.5],
  OM: [21.5, 55.9],
  NG: [9.1, 8.7],
  KE: [-0.02, 37.9],
  EG: [26.8, 30.8],
};

function project(lat, lon, W, H) {
  const clampedLat = Math.max(LAT_MIN, Math.min(LAT_MAX, lat));
  const x = ((lon + 180) / 360) * W;
  const y = ((LAT_MAX - clampedLat) / (LAT_MAX - LAT_MIN)) * H;
  return [x, y];
}

// Returns { key, latlon, label } — key groups rows that should share one
// bubble (e.g. two India-Karnataka rows, or two unresolved-city US rows).
function placementFor(row) {
  const country = String(row.country || "").toUpperCase().trim();
  const city = String(row.city || "").toLowerCase().trim();
  const region = String(row.region || "").toLowerCase().trim();

  if (country === "IN") {
    if (city && CITY_LATLON[city]) {
      return { key: `city:${city}`, latlon: CITY_LATLON[city], label: [row.city, row.region].filter(Boolean).join(", ") };
    }
    if (region && STATE_LATLON[region]) {
      return { key: `state:${region}`, latlon: STATE_LATLON[region], label: row.region };
    }
    return { key: "country:IN", latlon: COUNTRY_LATLON.IN, label: row.countryName || "India" };
  }

  if (country && COUNTRY_LATLON[country]) {
    return { key: `country:${country}`, latlon: COUNTRY_LATLON[country], label: row.countryName || country };
  }

  return null;
}

export default function WorldMap({ locations = [] }) {
  const W = 480;
  const H = 260;
  const byKey = new Map();
  let unmapped = 0;

  for (const row of locations) {
    const views = Number(row.views || 0);
    const placement = placementFor(row);
    if (!placement) {
      unmapped += views;
      continue;
    }
    const existing = byKey.get(placement.key);
    if (existing) {
      existing.views += views;
    } else {
      byKey.set(placement.key, { ...placement, views });
    }
  }

  const points = [...byKey.values()].map((p) => {
    const [x, y] = project(p.latlon[0], p.latlon[1], W, H);
    return { x, y, views: p.views, name: p.label || "Unknown" };
  });

  const maxViews = Math.max(1, ...points.map((p) => p.views));
  const radius = (v) => 5 + (Math.sqrt(v) / Math.sqrt(maxViews)) * 20;

  return (
    <div className="rzp-map">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Visitors around the world">
        <rect x="0" y="0" width={W} height={H} rx="14" className="rzp-map-bg" />
        {/* Reference graticule (equator + prime meridian) — plain lines, not a
            hand-drawn coastline, so we never render a landmass shape that's
            geographically wrong. */}
        <line x1={0} y1={project(0, 0, W, H)[1]} x2={W} y2={project(0, 0, W, H)[1]} className="rzp-map-grid" />
        <line x1={project(0, 0, W, H)[0]} y1={0} x2={project(0, 0, W, H)[0]} y2={H} className="rzp-map-grid" />
        {points
          .sort((a, b) => b.views - a.views)
          .map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={radius(p.views)} className="rzp-map-bubble">
              <title>{`${p.name}: ${p.views} views`}</title>
            </circle>
          ))}
      </svg>
      <p className="rzp-map-note">
        {points.length} location{points.length === 1 ? "" : "s"} plotted
        {unmapped > 0 ? ` · ${unmapped} views unmapped` : ""}
      </p>
    </div>
  );
}
